<template>
    <CompassPanel :value="showPanel" :title="translations.title" @beforeClose="closePanel">
        <template #default>
            <div v-if="template && currentStep" class="automation-wizard-panel__content">
                <AutomationWizardOverviewContainer
                    v-if="currentStep.type === 'overview'"
                    :template="template"
                />
                <AutomationWizardForm
                    v-else
                    v-model="formData"
                    :currentStep="currentStep"
                    @field-change="clearFieldError"
                />
            </div>
        </template>
        <template #footer>
            <AutomationWizardPanelFooter
                :allSteps="allSteps"
                :currentStepIndex="currentStepIndex"
                :isLoading="isLoadingActivate"
                @activate-automation="handleActivateAutomation"
                @previous-step="goToPreviousStep"
                @next-step="handleNextStep"
            />
        </template>
    </CompassPanel>
</template>

<script>
import AutomationWizardOverviewContainer from 'vcomponents/automation/templates/Wizard/Overview/AutomationWizardOverviewContainer.vue';
import AutomationWizardForm from 'vcomponents/automation/templates/Wizard/Form/AutomationWizardForm.vue';
import AutomationWizardPanelFooter from 'vcomponents/automation/templates/Wizard/AutomationWizardPanelFooter.vue';

import { useAutomationTemplate, enableAutomation } from 'api/automation/automationService.js';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    components: {
        AutomationWizardOverviewContainer,
        AutomationWizardForm,
        AutomationWizardPanelFooter,
    },
    props: {
        template: {
            type: Object,
            default: null,
        },
        showPanel: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationWizardPanel.title'),
            },
            isLoadingActivate: false,
            formData: {},
            currentStepIndex: 0,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        allSteps() {
            return this.template?.metadata?.wizard_steps || [];
        },
        currentStep() {
            return this.allSteps[this.currentStepIndex] || null;
        },
    },
    watch: {
        $route() {
            this.closePanel();
        },
        template() {
            this.currentStepIndex = 0;
            this.clearAllErrors();
            this.initializeFormData();
        },
        showPanel(newValue) {
            if (newValue) {
                this.currentStepIndex = 0;
                this.clearAllErrors();
                this.initializeFormData();
            }
        },
    },
    mounted() {
        this.initializeFormData();
    },
    methods: {
        closePanel() {
            this.clearAllErrors();
            this.$emit('close');
        },
        activateAutomation(id) {
            return enableAutomation(id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.start_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                });
        },
        async handleActivateAutomation() {
            this.isLoadingActivate = true;
            try {
                const preparedData = this.prepareFormDataForSubmission();
                const result = await useAutomationTemplate(this.template.id, preparedData);
                await this.activateAutomation(result.data.id);
                this.navigateToListing();
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
                throw error;
            } finally {
                this.isLoadingActivate = false;
            }
        },
        navigateToListing() {
            this.closePanel();
            this.$router.push({ name: 'automations.listing' });
        },
        goToPreviousStep() {
            if (this.currentStepIndex > 0) {
                this.currentStepIndex--;
            }
        },
        goToNextStep() {
            if (this.currentStepIndex < this.allSteps.length - 1) {
                this.currentStepIndex++;
            }
        },
        handleNextStep() {
            this.isLoadingActivate = true;
            const isLastStep = this.currentStepIndex === this.allSteps.length - 1;
            if (isLastStep) {
                if (this.validateCurrentStep()) {
                    this.handleActivateAutomation();
                } else {
                    this.isLoadingActivate = false;
                }
            } else if (this.validateCurrentStep()) {
                this.isLoadingActivate = false;
                this.goToNextStep();
            } else {
                this.isLoadingActivate = false;
            }
        },
        validateCurrentStep() {
            if (!this.currentStep || !this.currentStep.fields) {
                return true;
            }
            for (const field of this.currentStep.fields) {
                this.$delete(field, 'currentError');
            }
            const requiredFields = this.currentStep.fields.filter(
                (field) => field.required && field.type !== 'hidden',
            );
            let hasErrors = false;
            for (const field of requiredFields) {
                const fieldValue = this.formData[field.id];
                const isEmpty = this.isFieldValueEmpty(fieldValue);

                if (isEmpty) {
                    const errorMessage = field.props?.formItem?.error;
                    this.$set(field, 'currentError', errorMessage);
                    hasErrors = true;
                }
            }
            return !hasErrors;
        },
        isFieldValueEmpty(value) {
            return !value || (Array.isArray(value) && value.length === 0);
        },
        clearFieldError(fieldName) {
            if (!this.currentStep || !this.currentStep.fields) {
                return;
            }
            const field = this.currentStep.fields.find((f) => f.id === fieldName);
            if (field && field.currentError) {
                this.$delete(field, 'currentError');
            }
        },
        clearAllErrors() {
            if (!this.template?.metadata?.wizard_steps) {
                return;
            }
            for (const step of this.template.metadata.wizard_steps) {
                if (step.fields) {
                    for (const field of step.fields) {
                        if (field.currentError) {
                            this.$delete(field, 'currentError');
                        }
                    }
                }
            }
        },
        prepareFormDataForSubmission() {
            const templateData = JSON.parse(JSON.stringify(this.template.metadata));
            if (templateData.wizard_steps) {
                for (const step of templateData.wizard_steps) {
                    if (step.fields) {
                        for (const field of step.fields) {
                            if (
                                this.formData[field.id] !== undefined &&
                                this.formData[field.id] !== ''
                            ) {
                                field.value = this.formData[field.id];
                                if (field.props?.select) {
                                    field.props.select.value = this.formData[field.id];
                                }
                            }
                        }
                    }
                }
            }
            return {
                metadata: templateData,
            };
        },
        initializeFormData() {
            if (!this.template?.metadata?.wizard_steps) {
                this.formData = {};
                return;
            }
            const newFormData = {};
            for (const step of this.template.metadata.wizard_steps) {
                if (step.fields) {
                    for (const field of step.fields) {
                        let initialValue = '';
                        if (field.props?.select?.value !== undefined) {
                            initialValue = field.props.select.value;
                        }
                        newFormData[field.id] = initialValue;
                    }
                }
            }

            this.formData = newFormData;
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-panel {
    &__content {
        display: flex;
        flex-direction: column;
    }
}
</style>
